import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  unstable_useBlocker as useBlocker,
  useLocation,
  useParams,
} from "react-router";

import VLWizard, { WizardStep } from "views/uikit/VLWizard";
import CheckIcon from "assets/icons/CheckIcon";
import AddDataContent from "./Steps/AddDataContent";
import PreviewContent from "./Steps/PreviewContent";
import CreateDataset from "./Steps/CreateDataset";
import useDatasetWizardNavigation from "./useButtonConfigs";
import { DatasetStatus, FolderMetadata, FolderUploadStatus } from "types";
import {
  getFoldersWithLocalSelections,
  getFolderUploadStatus,
  getInvalidFolders,
  getMultiFolderUpload,
  getUploadingFolder,
} from "redux/CreateDataset/selectors";
import {
  fetchFolderMetadata,
  setLocalFolderSelections,
  startCreationStatusPolling,
  stopCreationStatusPolling,
} from "redux/CreateDataset/actions";
import abortControllerManager from "helpers/abortControllerManager";

import styles from "./style.module.scss";

const uploadingStatusMap: Record<FolderUploadStatus, boolean> = {
  [FolderUploadStatus.ACTIVE]: true,
  [FolderUploadStatus.COMPLETED]: false,
  [FolderUploadStatus.IDLE]: false,
  [FolderUploadStatus.FAILED]: false,
};

const getStepStatus = (currentStep: number, step: number) => {
  if (currentStep === step) return "process";
  if (currentStep > step) return "finish";
  return "wait";
};

const renderIcon = (status: string): JSX.Element | null => {
  if (status === "finish")
    return (
      <div className={styles.checkIconWrapper}>
        <CheckIcon variant="flat" />
      </div>
    );
  return null;
};

const getCurrentStepFromStatus = (
  isCreateMode: boolean,
  status?: DatasetStatus,
): number => {
  if (!status) {
    return 0;
  }
  if (isCreateMode) {
    return status !== DatasetStatus.DRAFT ? 2 : 0;
  }
  return status !== DatasetStatus.READ_ONLY ? 0 : 2;
};
interface DatasetCreationWizardProps {
  batchId?: string;
  status?: DatasetStatus;
}
const DatasetCreationWizard = ({
  batchId,
  status,
}: DatasetCreationWizardProps) => {
  const dispatch = useDispatch();
  const { newDatasetId } = useParams();
  const location = useLocation();
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  const [uploadedFolderNames, setUploadedFolderNames] = useState<string[]>([]);
  const isCreateMode = location.pathname.includes("/create");
  const isUpdateMode = location.pathname.includes("/update");

  const getCurrentStep = useCallback(
    () => getCurrentStepFromStatus(isCreateMode, status),
    [isCreateMode, status],
  );

  const [currentStep, setCurrentStep] = useState(getCurrentStep);

  // Track if batch was created at parent level
  const batchCreated = !!batchId;

  useEffect(() => {
    setCurrentStep(getCurrentStep());
  }, [getCurrentStep, isCreateMode, status]);

  // Separate effect for creation status polling to avoid interference
  useEffect(() => {
    if (!newDatasetId) return;

    // Step 2 (CreateDataset): Poll creation status when on this page
    if (currentStep === 2) {
      dispatch(startCreationStatusPolling(newDatasetId) as any);
    } else {
      dispatch(stopCreationStatusPolling() as any);
    }

    // Cleanup on unmount
    return () => {
      dispatch(stopCreationStatusPolling() as any);
    };
  }, [currentStep, newDatasetId, dispatch]);

  useEffect(() => {
    if (currentStep === 0 && newDatasetId && (!isUpdateMode || batchCreated)) {
      setIsTableLoading(true);
      dispatch(fetchFolderMetadata(newDatasetId) as any).finally(() =>
        setIsTableLoading(false),
      );
    }
  }, [currentStep, newDatasetId, isUpdateMode, batchCreated, dispatch]);

  const invalidFolders = useSelector(getInvalidFolders);
  const uploadingFolder = useSelector(getUploadingFolder);
  const folders = useSelector(getFoldersWithLocalSelections);
  const multiFolderUpload = useSelector(getMultiFolderUpload);
  const folderUploadStatus = useSelector(getFolderUploadStatus);

  const selectedFolderIds = useMemo(() => {
    return folders.filter((f) => f.selected).map((f) => f.id);
  }, [folders]);

  // Clean up uploaded folder names when they appear in metadata OR when they are invalid
  useEffect(() => {
    if (uploadedFolderNames.length > 0) {
      const existingFolderNames = folders.map((f) => f.folder_name);
      const invalidFolderNames = invalidFolders.map((f) => f.folder_name);

      // Remove folder names that are either in metadata or marked as invalid
      const stillPendingFolders = uploadedFolderNames.filter(
        (folderName) =>
          !existingFolderNames.includes(folderName) &&
          !invalidFolderNames.includes(folderName),
      );

      if (stillPendingFolders.length !== uploadedFolderNames.length) {
        setUploadedFolderNames(stillPendingFolders);
      }
    }
  }, [folders, invalidFolders, uploadedFolderNames]);

  const showUploadLoader = useMemo(() => {
    return uploadedFolderNames.length > 0;
  }, [uploadedFolderNames]);

  // Comprehensive upload detection
  const isUploadingActive = useMemo(() => {
    // Check if single folder upload is in progress
    const singleFolderUpload =
      uploadingStatusMap[folderUploadStatus] || uploadingFolder !== null;

    // Check if multi-folder upload is in progress
    const multiFolderUploadActive =
      multiFolderUpload !== null &&
      multiFolderUpload.status === FolderUploadStatus.ACTIVE;

    return singleFolderUpload || multiFolderUploadActive;
  }, [folderUploadStatus, uploadingFolder, multiFolderUpload]);

  // Add navigation guard when folder upload is active
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Show confirmation dialog when user tries to leave during upload
      const message =
        "Upload is in progress. Are you sure you want to leave? This will cancel the upload.";
      event.preventDefault();
      event.returnValue = message; // For older browsers
      return message;
    };

    if (isUploadingActive) {
      // Add beforeunload listener when upload is active
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    // Cleanup function
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isUploadingActive]);

  // Block React Router navigation when upload is active
  const blocker = useBlocker(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: any;
      nextLocation: any;
    }) => {
      // Block navigation if upload is in progress and trying to navigate away from current page
      return (
        isUploadingActive && currentLocation.pathname !== nextLocation.pathname
      );
    },
  );

  // Handle the blocked navigation with confirmation
  useEffect(() => {
    if (blocker.state === "blocked") {
      const proceed = window.confirm(
        "Upload is in progress. Are you sure you want to leave? This will cancel the upload.",
      );

      if (proceed) {
        // User confirmed - abort the upload and proceed with navigation
        abortControllerManager.abortController();
        blocker.proceed();
      } else {
        // User cancelled - reset the blocker
        blocker.reset();
      }
    }
  }, [blocker]);

  const handleFolderChange = useCallback(
    (updatedFolders: FolderMetadata[]) => {
      // Only consider valid folders for selection (exclude invalid folders)
      const validFolders = updatedFolders.filter((f) => f.status !== "invalid");
      const selectedIds = validFolders
        .filter((f) => f.selected)
        .map((f) => f.id);
      dispatch(setLocalFolderSelections(selectedIds));
    },
    [dispatch],
  );

  const handleFolderUploadStart = useCallback((folderNames: string[]) => {
    setUploadedFolderNames(folderNames);
  }, []);

  const buttonsConfig = useDatasetWizardNavigation({
    currentStep,
    selectedFolderIds,
    isFolderUploading: isUploadingActive,
    setCurrentStep,
    isUpdateMode,
    batchId,
  });

  const steps: WizardStep[] = useMemo(
    () => [
      {
        title: isUpdateMode ? "Add More Data" : "Add Data",
        status: getStepStatus(currentStep, 0),
        icon: renderIcon(getStepStatus(currentStep, 0)),
        disabled: false,
        content: (
          <AddDataContent
            folders={folders}
            isLoading={showUploadLoader}
            isTableLoading={isTableLoading}
            onFoldersChange={handleFolderChange}
            onFolderUploadStart={handleFolderUploadStart}
            batchId={batchId}
          />
        ),
      },
      {
        title: "Preview",
        status: getStepStatus(currentStep, 1),
        icon: renderIcon(getStepStatus(currentStep, 1)),
        disabled: true,
        content: <PreviewContent batchId={batchId} />,
      },
      {
        title: isUpdateMode ? "Update Dataset" : "Create Dataset",
        status: getStepStatus(currentStep, 2),
        icon: renderIcon(getStepStatus(currentStep, 2)),
        disabled: true,
        content: <CreateDataset />,
      },
    ],
    [
      isUpdateMode,
      currentStep,
      folders,
      showUploadLoader,
      isTableLoading,
      handleFolderChange,
      handleFolderUploadStart,
      batchId,
    ],
  );

  return (
    <VLWizard
      steps={steps}
      current={currentStep}
      buttonsConfig={buttonsConfig}
    />
  );
};

export default DatasetCreationWizard;
